import { SPOTLIGHT_COMPACT_BREAKPOINT, SPOTLIGHT_VIEWPORT_MARGIN } from "./spotlightTheme";

const CARD_GAP = 16;
const AUTO_PLACEMENT_ORDER = ["bottom", "top", "right", "left"];

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

export const isCompactViewport = (viewport) => viewport.width < SPOTLIGHT_COMPACT_BREAKPOINT;

/** Grows `rect` by `padding` and keeps it inside the viewport. */
export const getHoleRect = (rect, padding, viewport) => {
	const left = clamp(rect.left - padding, 0, viewport.width);
	const top = clamp(rect.top - padding, 0, viewport.height);
	const right = clamp(rect.right + padding, 0, viewport.width);
	const bottom = clamp(rect.bottom + padding, 0, viewport.height);
	return { left, top, right, bottom, width: right - left, height: bottom - top };
};

/** The four rectangles that dim everything except `hole`. */
export const getBlockerRects = (hole, viewport) => [
	{ key: "top", left: 0, top: 0, width: viewport.width, height: hole.top },
	{ key: "bottom", left: 0, top: hole.bottom, width: viewport.width, height: viewport.height - hole.bottom },
	{ key: "left", left: 0, top: hole.top, width: hole.left, height: hole.height },
	{ key: "right", left: hole.right, top: hole.top, width: viewport.width - hole.right, height: hole.height },
];

const getAvailableSpace = (hole, viewport) => ({
	bottom: viewport.height - hole.bottom - CARD_GAP - SPOTLIGHT_VIEWPORT_MARGIN,
	top: hole.top - CARD_GAP - SPOTLIGHT_VIEWPORT_MARGIN,
	right: viewport.width - hole.right - CARD_GAP - SPOTLIGHT_VIEWPORT_MARGIN,
	left: hole.left - CARD_GAP - SPOTLIGHT_VIEWPORT_MARGIN,
});

const fits = (side, space, card) => (side === "top" || side === "bottom" ? card.height <= space[side] : card.width <= space[side]);

const placeOnSide = (side, hole, card, viewport) => {
	const maxLeft = viewport.width - card.width - SPOTLIGHT_VIEWPORT_MARGIN;
	const maxTop = viewport.height - card.height - SPOTLIGHT_VIEWPORT_MARGIN;
	const centeredLeft = clamp(hole.left + hole.width / 2 - card.width / 2, SPOTLIGHT_VIEWPORT_MARGIN, maxLeft);
	const centeredTop = clamp(hole.top + hole.height / 2 - card.height / 2, SPOTLIGHT_VIEWPORT_MARGIN, maxTop);

	if (side === "bottom") return { side, left: centeredLeft, top: hole.bottom + CARD_GAP };
	if (side === "top") return { side, left: centeredLeft, top: hole.top - CARD_GAP - card.height };
	if (side === "right") return { side, left: hole.right + CARD_GAP, top: centeredTop };
	return { side, left: hole.left - CARD_GAP - card.width, top: centeredTop };
};

const placeAtViewportCenter = (card, viewport) => ({
	side: "center",
	left: Math.max((viewport.width - card.width) / 2, SPOTLIGHT_VIEWPORT_MARGIN),
	top: Math.max((viewport.height - card.height) / 2, SPOTLIGHT_VIEWPORT_MARGIN),
});

/**
 * Picks where the tutorial card sits relative to the highlighted hole.
 * Preferred side first (if it fits), then the first side with room, otherwise the roomier of top/bottom.
 * Compact (mobile) viewports only ever use top/bottom so the card can span the screen width.
 */
export const computeCardPosition = ({ hole, card, viewport, placement = "auto" }) => {
	if (!hole || placement === "center") return placeAtViewportCenter(card, viewport);

	const space = getAvailableSpace(hole, viewport);
	const allowedSides = isCompactViewport(viewport) ? ["bottom", "top"] : AUTO_PLACEMENT_ORDER;
	const preferredSides = allowedSides.includes(placement) ? [placement] : [];
	const candidateSides = [...preferredSides, ...allowedSides];

	const side = candidateSides.find((candidate) => fits(candidate, space, card));
	if (side) return placeOnSide(side, hole, card, viewport);

	const roomierVerticalSide = space.bottom >= space.top ? "bottom" : "top";
	const position = placeOnSide(roomierVerticalSide, hole, card, viewport);
	return { ...position, top: clamp(position.top, SPOTLIGHT_VIEWPORT_MARGIN, viewport.height - card.height - SPOTLIGHT_VIEWPORT_MARGIN) };
};
